import { useCallback, useEffect, useState } from "react";
import { api } from "./api";
import { ApplicationsView, EnquiriesView, PositionsView, VenturesView } from "./AdminData";
import { Pager } from "./shared";
import "./admin.css";

const NAV = [
  ["dashboard", "Dashboard"], ["enquiries", "Enquiries"], ["applications", "Applications"],
  ["ventures", "Ventures"], ["positions", "Positions"], ["settings", "Settings"], ["activity", "Activity"],
];

function DashboardView() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api("/admin/stats").then(setStats).catch(() => {}); }, []);
  if (!stats) return <p className="admin-muted">Loading…</p>;
  const cards = [
    ["Total enquiries", stats.total_enquiries], ["New enquiries", stats.new_enquiries],
    ["Applications", stats.total_applications], ["New applications", stats.new_applications],
    ["Open positions", stats.open_positions], ["Active ventures", stats.active_ventures],
  ];
  return (
    <div data-testid="dashboard-view">
      <div className="admin-stat-grid">
        {cards.map(([label, value]) => (
          <div className="admin-stat-card" key={label} data-testid={`stat-${label.toLowerCase().replaceAll(" ", "-")}`}>
            <span>{label}</span><strong>{value}</strong>
          </div>
        ))}
      </div>
      <h3 className="admin-section-title">Recent activity</h3>
      <table className="admin-table" data-testid="recent-activity-table">
        <thead><tr><th>When</th><th>Action</th><th>Target</th></tr></thead>
        <tbody>
          {stats.recent_activity.map((entry) => (
            <tr key={entry.id}>
              <td>{new Date(entry.created_at).toLocaleString()}</td>
              <td>{entry.action}</td>
              <td>{entry.target_type || "—"}</td>
            </tr>
          ))}
          {!stats.recent_activity.length && <tr><td colSpan="3" className="admin-muted">No activity yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function SettingsView() {
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [saved, setSaved] = useState("");
  const load = useCallback(async () => { setItems(await api("/admin/settings")); }, []);
  useEffect(() => { load(); }, [load]);
  const save = async (key) => {
    await api("/admin/settings", { method: "PATCH", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ key, value: drafts[key] ?? "" }) });
    setSaved(key); setTimeout(() => setSaved(""), 2000); load();
  };
  return (
    <div data-testid="settings-view">
      <table className="admin-table" data-testid="settings-table">
        <thead><tr><th>Key</th><th>Value</th><th></th></tr></thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id} data-testid={`setting-row-${item.key}`}>
              <td>{item.key}</td>
              <td><input defaultValue={item.value || ""}
                onChange={(e) => setDrafts({ ...drafts, [item.key]: e.target.value })}
                data-testid={`setting-input-${item.key}`} /></td>
              <td><button onClick={() => save(item.key)} data-testid={`setting-save-${item.key}`}>
                {saved === item.key ? "Saved ✓" : "Save"}</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ActivityView() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 20 });
  const [page, setPage] = useState(1);
  useEffect(() => { api(`/admin/audit-logs?page=${page}`).then(setData).catch(() => {}); }, [page]);
  return (
    <div data-testid="activity-view">
      <table className="admin-table" data-testid="activity-table">
        <thead><tr><th>When</th><th>Action</th><th>Target type</th><th>Details</th></tr></thead>
        <tbody>
          {data.items.map((entry) => (
            <tr key={entry.id}>
              <td>{new Date(entry.created_at).toLocaleString()}</td>
              <td>{entry.action}</td><td>{entry.target_type || "—"}</td>
              <td className="admin-clip">{Object.keys(entry.metadata || {}).length ? JSON.stringify(entry.metadata) : "—"}</td>
            </tr>
          ))}
          {!data.items.length && <tr><td colSpan="4" className="admin-muted">No activity yet.</td></tr>}
        </tbody>
      </table>
      <Pager page={page} total={data.total} pageSize={data.page_size} onPage={setPage} />
    </div>
  );
}

export default function AdminApp() {
  const [user, setUser] = useState(undefined);
  const [view, setView] = useState("dashboard");

  useEffect(() => {
    api("/auth/me").then((u) => {
      if (u.role !== "admin") { window.location.href = "/admin/login"; return; }
      setUser(u);
    }).catch(() => {});
  }, []);

  const logout = async () => {
    await api("/auth/logout", { method: "POST" }).catch(() => {});
    window.location.href = "/admin/login";
  };

  if (user === undefined) {
    return <div className="admin-shell admin-loading" data-testid="admin-loading">Loading…</div>;
  }

  const views = {
    dashboard: <DashboardView />, enquiries: <EnquiriesView />, applications: <ApplicationsView />,
    ventures: <VenturesView />, positions: <PositionsView />, settings: <SettingsView />, activity: <ActivityView />,
  };

  return (
    <div className="admin-shell" data-testid="admin-dashboard">
      <aside className="admin-sidebar">
        <a className="wordmark" href="/" data-testid="admin-wordmark"><span className="wordmark-dot" />ALCRISTAR</a>
        <nav>
          {NAV.map(([key, label]) => (
            <button key={key} className={view === key ? "current" : ""} onClick={() => setView(key)}
              data-testid={`admin-nav-${key}`}>{label}</button>
          ))}
        </nav>
        <div className="admin-sidebar-foot">
          <span className="admin-muted">{user.email}</span>
          <button onClick={logout} data-testid="admin-logout-button">Log out</button>
        </div>
      </aside>
      <main className="admin-main">
        <header className="admin-main-head">
          <h2>{NAV.find(([key]) => key === view)?.[1]}</h2>
        </header>
        {views[view]}
      </main>
    </div>
  );
}
